import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Backdrop
} from "@mui/material"
import AppPagination from "components/StyledComponents/AppPagination"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { useDispatch, useSelector } from "react-redux"
import { useTheme } from "@mui/system"
import TierForm from "./components/TierForm"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import Card from "components/Card/Card"
import ToggleSwitch from "components/StyledComponents/ToggleSwitch"
import {
  createTierAction,
  deleteTierAction,
  getTiersAction,
  updateRewardStateAction,
  updateTierAction
} from "redux/superAdmin/actions"
import { unlockLogicLabel } from "helpers/functions"
import { tierColumns } from "helpers/constant"
import { Formik } from "formik"
import { validationSchemaTier } from "helpers/validationSchema"
import TableNoRecords from "components/TableNoRecords"
import { toast } from "react-toastify"

const StatusText = ({ active }) => (
  <Typography
    variant="caption3"
    sx={{
      color: active ? "#4CAF50" : "#9E9E9E",
      fontFamily: "Inter-Regular",
      fontWeight: 600
    }}>
    {active ? "Active" : "Inactive"}
  </Typography>
)

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}))

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "16px 24px 16px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))

const ActionsMenu = ({ onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const openMenu = Boolean(anchorEl)
  const theme = useTheme()

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: "text.secondary" }}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Backdrop
        sx={{ color: theme.palette.white.main, zIndex: 1300 }}
        open={openMenu}
        onClick={handleMenuClose}>
        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          PaperProps={{
            sx: {
              marginTop: "15px",
              backgroundColor: theme.palette.background.default,
              borderRadius: "12px",
              "& .MuiList-root": {
                backgroundColor: theme.palette.background.default,
                width: "150px",
                boxSizing: "border-box",
                padding: "10px 0"
              }
            }
          }}>
          <StyledMenuItem
            onClick={() => {
              handleMenuClose()
              onEdit()
            }}>
            <Typography variant="body1Regular">Edit</Typography>
          </StyledMenuItem>
          <StyledMenuItem
            onClick={() => {
              handleMenuClose()
              onDelete()
            }}>
            <Typography sx={{ color: theme.palette.red.main }} variant="body1Regular">
              Delete
            </Typography>
          </StyledMenuItem>
        </Menu>
      </Backdrop>
    </>
  )
}

const TierManagement = ({ addTrigger = 0 }) => {
  const { userRestaurant } = useSelector((state) => state.user)
  const restaurantId = userRestaurant?.id
  const { tiers, downgradeEnabled } = useSelector((state) => state.superAdmin)
  const dispatch = useDispatch()
  const [formOpen, setFormOpen] = useState(false)
  const [selectedTier, setSelectedTier] = useState(null)
  const [isEdit, setIsEdit] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [tierToDelete, setTierToDelete] = useState(null)
  const [page, setPage] = useState(0)
  const { isLoading } = useSelector((state) => state.app)
  const rowsPerPage = 10

  useEffect(() => {
    if (!restaurantId) return
    dispatch(getTiersAction({ restaurantId }))
  }, [restaurantId])

  useEffect(() => {
    if (addTrigger > 0) {
      setSelectedTier(null)
      setIsEdit(false)
      setFormOpen(true)
    }
  }, [addTrigger])

  const initialValues = {
    name: "",
    coin_threshold: "",
    unlock_logic: "coinsOnly",
    streak_requirement: null,
    is_active: true
  }

  const initialValuesEdit = {
    name: selectedTier?.name,
    coin_threshold: selectedTier?.coin_threshold,
    unlock_logic: selectedTier?.unlock_logic,
    streak_requirement: selectedTier?.streak_requirement,
    is_active: selectedTier?.is_active !== false
  }

  const handleSubmit = (formData) => {
    const goldTier = tiers.find((t) => t.name.toLowerCase() === "gold" && t.id !== selectedTier?.id)
    const silverTier = tiers.find(
      (t) => t.name.toLowerCase() === "silver" && t.id !== selectedTier?.id
    )
    const bronzeTier = tiers.find(
      (t) => t.name.toLowerCase() === "bronze" && t.id !== selectedTier?.id
    )

    const currentName = formData.name.toLowerCase()
    const currentCoins = Number(formData.coin_threshold) || 0
    const currentStreak = Number(formData.streak_requirement) || 0

    if (currentName === "gold") {
      if (
        silverTier &&
        ((currentCoins > 0 &&
          silverTier.coin_threshold > 0 &&
          currentCoins <= silverTier.coin_threshold) ||
          (currentStreak > 0 &&
            silverTier.streak_requirement > 0 &&
            currentStreak <= silverTier.streak_requirement))
      ) {
        return toast.error("Gold tier requirements must be higher than Silver")
      }
      if (
        bronzeTier &&
        ((currentCoins > 0 &&
          bronzeTier.coin_threshold > 0 &&
          currentCoins <= bronzeTier.coin_threshold) ||
          (currentStreak > 0 &&
            bronzeTier.streak_requirement > 0 &&
            currentStreak <= bronzeTier.streak_requirement))
      ) {
        return toast.error("Gold tier requirements must be higher than Bronze")
      }
    } else if (currentName === "silver") {
      if (
        goldTier &&
        ((currentCoins > 0 &&
          goldTier.coin_threshold > 0 &&
          currentCoins >= goldTier.coin_threshold) ||
          (currentStreak > 0 &&
            goldTier.streak_requirement > 0 &&
            currentStreak >= goldTier.streak_requirement))
      ) {
        return toast.error("Silver tier requirements must be lower than Gold")
      }
      if (
        bronzeTier &&
        ((currentCoins > 0 &&
          bronzeTier.coin_threshold > 0 &&
          currentCoins <= bronzeTier.coin_threshold) ||
          (currentStreak > 0 &&
            bronzeTier.streak_requirement > 0 &&
            currentStreak <= bronzeTier.streak_requirement))
      ) {
        return toast.error("Silver tier requirements must be higher than Bronze")
      }
    } else if (currentName === "bronze") {
      if (
        goldTier &&
        ((currentCoins > 0 &&
          goldTier.coin_threshold > 0 &&
          currentCoins >= goldTier.coin_threshold) ||
          (currentStreak > 0 &&
            goldTier.streak_requirement > 0 &&
            currentStreak >= goldTier.streak_requirement))
      ) {
        return toast.error("Bronze tier requirements must be lower than Gold")
      }
      if (
        silverTier &&
        ((currentCoins > 0 &&
          silverTier.coin_threshold > 0 &&
          currentCoins >= silverTier.coin_threshold) ||
          (currentStreak > 0 &&
            silverTier.streak_requirement > 0 &&
            currentStreak >= silverTier.streak_requirement))
      ) {
        return toast.error("Bronze tier requirements must be lower than Silver")
      }
    }

    const payload = { ...formData, restaurant_id: restaurantId }
    const params = isEdit
      ? { tierId: selectedTier.id, payload, callback: onSuccess }
      : { payload, callback: onSuccess }
    if (isEdit) {
      dispatch(updateTierAction(params))
    } else {
      dispatch(createTierAction(params))
    }
  }

  const onSuccess = () => {
    setFormOpen(false)
    setSelectedTier(null)
    setIsEdit(false)
  }

  const handleConfirmDelete = () => {
    dispatch(
      deleteTierAction({
        tierId: tierToDelete.id,
        restaurantId,
        callback: () => {
          setDeleteDialogOpen(false)
          setTierToDelete(null)
        }
      })
    )
  }

  const handleDowngradeToggle = (event) => {
    const newValue = event.target.checked
    dispatch(
      updateRewardStateAction({
        payload: {
          restaurant_id: restaurantId,
          downgrade_enabled: newValue
        }
      })
    )
  }

  return (
    <Box>
      <Card style={{ marginBottom: 24 }}>
        <FormControlLabel
          control={<ToggleSwitch checked={downgradeEnabled} onChange={handleDowngradeToggle} />}
          label={
            <Box sx={{ ml: 1 }}>
              <Typography variant="body1Medium" sx={{ color: "text.primary", display: "block" }}>
                Allow Tier Downgrades
              </Typography>
              <Typography variant="body2Regular" color="textSecondary">
                {"Allows users to drop to lower tiers if they don't meet tier requirements"}
              </Typography>
            </Box>
          }
        />
      </Card>

      <Paper sx={{ mt: 3 }}>
        <TableContainer sx={{ maxHeight: 700 }}>
          <CustomTable stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography
                    variant="body2Regular"
                    style={{ opacity: 1, display: "flex", alignItems: "center", gap: "4px" }}>
                    #
                  </Typography>
                </TableCell>
                {tierColumns?.map((col) => (
                  <TableCell key={col} align={col === "Actions" ? "right" : "left"}>
                    <Typography variant="body2Regular" style={{ opacity: 0.6 }}>
                      {col}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tiers?.length ? (
                tiers?.map((tier, idx) => {
                  const seqId = String(page * rowsPerPage + idx + 1)
                  return (
                    <TableRow
                      key={tier.id}
                      sx={!tier.is_active ? { opacity: "0.6" } : { opacity: "1" }}>
                      <TableCell>
                        <Typography variant="body2Regular">{seqId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2Regular">{tier.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2Regular">
                          {unlockLogicLabel(tier.unlock_logic)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusText active={tier.is_active} />
                      </TableCell>
                      <TableCell align="right">
                        <ActionsMenu
                          onEdit={() => {
                            setSelectedTier(tier)
                            setIsEdit(true)
                            setFormOpen(true)
                          }}
                          onDelete={() => {
                            setTierToDelete(tier)
                            setDeleteDialogOpen(true)
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })
              ) : (
                <TableNoRecords
                  colSpan={tierColumns.length}
                  label={
                    isLoading
                      ? "Please wait while we are gettng the tiers..."
                      : "No Tiers configured yet."
                  }
                />
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
        <AppPagination
          onPageChange={(p) => setPage(p - 1)}
          totalPages={Math.ceil(tiers?.length / rowsPerPage)}
          currentPage={page + 1}
        />
      </Paper>
      {/* )} */}

      {formOpen && (
        <Formik
          onSubmit={handleSubmit}
          validationSchema={validationSchemaTier}
          initialValues={selectedTier ? initialValuesEdit : initialValues}
          enableReinitialize>
          {(formik) => (
            <TierForm
              open={formOpen}
              onClose={() => {
                setFormOpen(false)
                setSelectedTier(null)
                setIsEdit(false)
              }}
              formik={formik}
              tier={selectedTier}
              isEdit={isEdit}
              loading={isLoading}
            />
          )}
        </Formik>
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setTierToDelete(null)
        }}
        onDelete={handleConfirmDelete}
        title="Delete Tier"
        description={`Are you sure you want to delete the "${tierToDelete?.name}" tier ? This cannot be undone.`}
        btnText="Delete"
      />
    </Box>
  )
}

export default TierManagement
